import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  HelpPanel,
  Link,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react-v3";
import Skeleton from "@amzn/awsui-components-console/skeleton";
import { User } from "../services/idp/idp.types";
import { UserStatusIndicator } from "./user-status-indicator";
import { LastAuthenticationBadge } from "./last-authentication-badge";
import { SupportUrls } from "../lib/constants";

interface UserOverviewProps {
  user?: User;
  showSkeleton: boolean;
  onInfoClick: (panel: React.ReactNode) => void;
}

const headerTitle = "Overview";

const helpPanel = (
  <HelpPanel header={<Header variant="h2">User Overview</Header>}>
    <Box variant="h5">Account status</Box>
    <p>
      User account statuses include enabled, locked, and disabled. Accounts are
      locked for 30 minutes following too many failed authentication attempts
      and are disabled indefinitely when a user leaves Amazon or Leo.
    </p>
    <Box variant="h5">Last seen</Box>
    <p>
      How long it has been since the last successful authentication request. A
      typical authentication request provides 24 hours of access. You can hover
      over the badge to see the exact authentication time.
    </p>
    <p>
      Note that after 90 days without a successful authentication request, all
      FIDO2 and OTP credentials will be revoked and require re-registration.
    </p>
    <Box variant="h5">OTP status</Box>
    <p>
      OTP access is controlled through group membership and is currently heavily
      restricted to specific use cases within Leo. If you have a use case for
      OTP or otherwise feel OTP should be enabled, please{" "}
      <Link external href={SupportUrls.ACCESS_ISSUE}>
        open a ticket
      </Link>
      .
    </p>
  </HelpPanel>
);

const UserOverviewSkeleton = () => (
  <Container
    data-testid="user-overview-skeleton"
    header={<Header variant="h3">{headerTitle}</Header>}
  >
    <ColumnLayout columns={3} variant="text-grid">
      <Skeleton variant="text-body-m" width="60%" />
      <Skeleton variant="text-body-m" width="40%" />
      <Skeleton variant="text-body-m" width="50%" />
    </ColumnLayout>
  </Container>
);

export const UserOverview = ({
  user,
  showSkeleton,
  onInfoClick,
}: UserOverviewProps) => {
  if (showSkeleton || !user) {
    return <UserOverviewSkeleton />;
  }

  return (
    <Container
      header={
        <Header
          variant="h3"
          info={
            <Button
              variant="inline-link"
              onClick={() => onInfoClick(helpPanel)}
            >
              Info
            </Button>
          }
        >
          {headerTitle}
        </Header>
      }
    >
      <ColumnLayout
        columns={user.otpEnabled ? 4 : 3}
        variant="text-grid"
        minColumnWidth={50}
      >
        <div key="status">
          <UserStatusIndicator prependString="Account is" user={user} />
        </div>
        <div key="last-seen">
          <SpaceBetween direction="horizontal" size="xs">
            <span>Last seen</span>
            <LastAuthenticationBadge
              time={user.lastAuthSuccess}
              userStatus={user.status}
            />
          </SpaceBetween>
        </div>
        <div key="otp-status">
          <StatusIndicator
            type={user.otpEnabled ? "success" : "stopped"}
            data-testid="otp-status"
          >
            OTP is {user.otpEnabled ? "enabled" : "disabled"}
          </StatusIndicator>
        </div>
        {user.otpEnabled && (
          <div key="otp-pin-status">
            <StatusIndicator
              type={user.otpPinSet ? "success" : "warning"}
              data-testid="otp-pin-status"
            >
              OTP PIN is {user.otpPinSet ? "" : "not "}set
            </StatusIndicator>
          </div>
        )}
      </ColumnLayout>
    </Container>
  );
};
